/**
 * Age validation utilities for the join funnel Step 2.
 *
 * Pure functions, no React. Edge-case aware (leap days, future DOBs).
 */

import { MAX_PLAUSIBLE_AGE, MIN_AGE, SOFT_AGE_WARNING_THRESHOLD } from '@/constants/funnel';

export type AgeValidationStatus = 'ok' | 'under_min' | 'over_soft_threshold' | 'invalid';

export interface AgeValidationResult {
  status: AgeValidationStatus;
  /** Computed age in years, or null when DOB is missing/invalid. */
  age: number | null;
}

/**
 * Compute integer age from a date of birth, anchored to a reference date.
 * Returns null if the DOB is in the future or fails to parse.
 *
 * Uses UTC accessors throughout so DOB comparisons are timezone-stable: a
 * DOB string like "1990-04-01" parses as UTC midnight, and we anchor "now"
 * to UTC too. Without this, a US-local Date for that DOB rolls back to
 * 1990-03-31 and the birthday-hasn't-happened-yet check fires erroneously.
 */
export function computeAge(dob: Date | string, now: Date = new Date()): number | null {
  const dobDate = typeof dob === 'string' ? new Date(dob) : dob;
  if (Number.isNaN(dobDate.getTime())) return null;
  if (dobDate.getTime() > now.getTime()) return null;

  let age = now.getUTCFullYear() - dobDate.getUTCFullYear();
  const monthDiff = now.getUTCMonth() - dobDate.getUTCMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getUTCDate() < dobDate.getUTCDate())) {
    age -= 1;
  }
  return age;
}

/**
 * Validate a DOB against the funnel's age rules.
 *
 * - `under_min` → blocking (Continue disabled, copy: "must be 18 or older")
 * - `over_soft_threshold` → soft warning (Continue allowed, copy: "may not
 *   apply well at your age"), also fired when DOB yields an implausible age
 *   beyond MAX_PLAUSIBLE_AGE (likely a typo)
 * - `invalid` → DOB is malformed or in the future
 * - `ok` → no warning needed
 */
export function validateAge(dob: Date | string, now: Date = new Date()): AgeValidationResult {
  const age = computeAge(dob, now);
  if (age === null) return { status: 'invalid', age: null };
  if (age < MIN_AGE) return { status: 'under_min', age };
  if (age >= SOFT_AGE_WARNING_THRESHOLD || age > MAX_PLAUSIBLE_AGE) {
    return { status: 'over_soft_threshold', age };
  }
  return { status: 'ok', age };
}

/**
 * Parse an MM/DD/YYYY string into a Date, returning null on any malformed
 * input. Tolerates either '/' or '-' separators and zero-padding variations.
 */
export function parseMmDdYyyy(input: string): Date | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (!match) return null;
  const [, mm, dd, yyyy] = match;
  const month = Number(mm);
  const day = Number(dd);
  const year = Number(yyyy);
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  // Reject 02/30 etc. — the Date constructor would silently roll over.
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return date;
}

/**
 * Format a Date as ISO 8601 calendar date (YYYY-MM-DD), suitable for the
 * `dateOfBirth` field on the funnel lead PATCH endpoint.
 */
export function formatIsoDate(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
